import React, { useState, useEffect } from 'react';
import { validateQRData, generateQRData } from '../../services/simpleEmployeeService';
import { Scanner } from '@yudiel/react-qr-scanner';
import { ScanResult } from './ScanResult';
import styles from './RegisterScanner.module.css';

export const RegisterScanner: React.FC = () => {
  const [qrValue, setQrValue] = useState<string>('');
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState<boolean | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  // Manual Entry State
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');

  // Check if we're in a secure context (HTTPS or localhost)
  useEffect(() => {
    const isSecureContext = window.isSecureContext;
    if (!isSecureContext) {
      setError('Camera access requires HTTPS. Please use a secure connection.');
      setCameraAvailable(false);
      return;
    }

    // Check if camera is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera not available on this device.');
      setCameraAvailable(false);
      return;
    }

    setCameraAvailable(true);
  }, []);

  const handleQRScan = async (result: any) => {
    // Handle different possible return formats from the scanner library
    let scannedValue = '';

    if (typeof result === 'string') {
      scannedValue = result;
    } else if (Array.isArray(result)) {
      if (result.length > 0) {
        scannedValue = result[0]?.rawValue || result[0]?.value || result[0] || '';
      }
    } else if (result?.rawValue) {
      scannedValue = result.rawValue;
    } else if (result?.value) {
      scannedValue = result.value;
    }

    if (!scannedValue) return;

    try {
      setScanning(false);

      // Validate QR code
      const validation = validateQRData(scannedValue);

      if (!validation.valid) {
        setError('Invalid QR code format');
        setScanning(true);
        return;
      }

      // Valid QR code - show ScanResult
      setQrValue(scannedValue);
      setError(null);
      setShowResult(true);
    } catch (err: any) {
      console.error('[SCAN] Error handling scan:', err);
      setError('Failed to scan QR code: ' + err.message);
      setScanning(true);
    }
  };

  const handleScanError = (error: any) => {
    // Check if it's a permission error
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      setPermissionDenied(true);
      setError('Camera permission denied. Please allow camera access in your browser settings.');
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      setError('No camera found on this device.');
    } else {
      // Ignore minor scanning errors that happen frequently
      if (error?.message?.includes('No QR code found')) return;
      console.warn('[SCAN] Camera error:', error);
    }
  };

  const handleBackToScanner = () => {
    setShowResult(false);
    setQrValue('');
    setScanning(true);
    setError(null);
    setPermissionDenied(false);
    setShowManualEntry(false);
    setManualCode('');
  };

  const handleRequestPermission = async () => {
    try {
      setScanning(true);
      setPermissionDenied(false);
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      // Stop the stream immediately - we just wanted to trigger permission
      stream.getTracks().forEach(track => track.stop());
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setPermissionDenied(true);
        setError('Camera permission denied.');
      } else {
        setError('Failed to access camera: ' + err.message);
      }
      setScanning(false);
    }
  };

  const handleManualSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!manualCode.trim()) return;

    try {
      const validation = validateQRData(manualCode);
      if (!validation.valid) {
        setError('Invalid code format. Please check the code.');
        return;
      }
      setQrValue(manualCode);
      setShowResult(true);
      setError(null);
      setShowManualEntry(false);
    } catch (err) {
      setError('Invalid code format');
    }
  };

  // Show ScanResult component when a QR is scanned
  if (showResult && qrValue) {
    return <ScanResult qrValue={qrValue} onBack={handleBackToScanner} />;
  }

  return (
    <div className={styles.scanner}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>COMP REDEMPTION</h1>
          <p>Scan employee QR or enter code manually</p>
        </div>

        {/* Manual Entry Modal Overlay */}
        {showManualEntry && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              width: '100%',
              maxWidth: '400px',
              border: '1px solid var(--accent-cyan)',
              padding: '30px',
              background: '#000',
              boxShadow: 'var(--glow-cyan)'
            }}>
              <h3 style={{ color: 'var(--accent-cyan)', marginBottom: '20px', fontSize: '1.5rem' }}>MANUAL ENTRY</h3>
              <form onSubmit={handleManualSubmit}>
                <textarea
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Paste QR code data here..."
                  style={{
                    width: '100%',
                    height: '100px',
                    background: '#111',
                    border: '1px solid #333',
                    color: '#fff',
                    fontFamily: 'monospace',
                    padding: '10px',
                    marginBottom: '20px',
                    resize: 'none'
                  }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    type="button"
                    onClick={() => setShowManualEntry(false)}
                    className={styles.button}
                    style={{ flex: 1, borderColor: '#666', color: '#888' }}
                  >
                    CANCEL
                  </button>
                  <button 
                    type="submit"
                    className={styles.button}
                    style={{ flex: 1, borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)' }}
                  >
                    SUBMIT
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Camera availability check */}
        {cameraAvailable === false && !showManualEntry && (
          <div className={styles.error}>
            <strong>SETUP REQUIRED:</strong> {error}
            <div className={styles.errorHint}>
              This scanner requires a secure HTTPS connection and camera access.
            </div>
          </div>
        )}

        {/* Permission denied UI */}
        {permissionDenied && !showManualEntry && (
          <div className={styles.permissionDenied}>
            <div className={styles.permissionIcon}>📱</div>
            <h3>CAMERA PERMISSION REQUIRED</h3>
            <p>To scan QR codes, you need to allow camera access.</p>
            <button
              onClick={handleRequestPermission}
              className={styles.permissionButton}
            >
              REQUEST PERMISSION
            </button>
          </div>
        )}

        {error && !permissionDenied && cameraAvailable !== false && !showManualEntry && (
          <div className={styles.error}>
            <strong>ERROR:</strong> {error}
          </div>
        )}

        <div className={styles.scannerContainer}>
          {scanning && cameraAvailable !== false && !permissionDenied && !showManualEntry ? (
            <Scanner
              onScan={handleQRScan}
              onError={handleScanError}
              constraints={{
                facingMode: { ideal: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }}
              scanDelay={500}
              allowMultiple={true} 
            />
          ) : (
            <div className={styles.scanArea}>
              <div className={styles.scanPlaceholder}>
                {permissionDenied ? '📷 PERMISSION REQUIRED' :
                 cameraAvailable === false ? '⚠️ CAMERA UNAVAILABLE' :
                 showManualEntry ? '⌨️ MANUAL ENTRY' :
                 '📷 SCANNER PAUSED'}
              </div>
            </div>
          )}
        </div>

        <div className={styles.controls}>
          <button
            onClick={() => {
              setScanning(true);
              setError(null);
              setPermissionDenied(false);
            }}
            className={styles.button}
            disabled={cameraAvailable === false}
          >
            RETRY SCAN
          </button>
          <button
            onClick={() => {
              setScanning(false);
              setShowManualEntry(true);
              setError(null);
            }}
            className={styles.button}
          >
            MANUAL ENTER
          </button>
          <button
            onClick={() => {
              // Generate and show a test QR code in console
              const testId = '00000000-0000-0000-0000-000000000000';
              const testQR = generateQRData(testId);
              console.log('[SCAN] TEST MODE - Generated QR for test employee');
              console.log(testQR);
              alert('Check console for Test QR string to copy/paste');
            }}
            className={styles.button}
            style={{ opacity: 0.5 }}
          >
            TEST QR
          </button>
        </div>

        <div className={styles.instructions}>
          <h2>HOW TO USE</h2>
          <p>1. Point camera at employee QR code</p>
          <p>2. Wait for scan to complete</p>
          <p>3. Choose comp type to redeem</p>
          
          <div className={styles.tips}>
            <h3>Tips:</h3>
            <ul>
              <li>Ensure QR code is well-lit</li>
              <li>QR codes expire (check timestamp)</li>
              <li>Each comp is personal</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
